import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Field, useField } from "formik";
import { ReactNode } from "react";

interface InputTextProps {
  name: string;
  label: ReactNode;
  moreinfo?: string;
  type?: string;
  ssn?: boolean;
  autocomplete?: string;
  maxLength?: number;
  numbersOnly?: boolean;
  numbersAndDecimalsOnly?: boolean;
  upperCase?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

const InputText = ({
  name,
  label,
  moreinfo,
  type = "text",
  ssn,
  autocomplete = "off",
  maxLength,
  numbersOnly = false,
  numbersAndDecimalsOnly = false,
  upperCase = false,
  inputMode,
}: InputTextProps) => {
  const [field, meta, helpers] = useField(name);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (ssn) {
      const ssnVal = e.target.value;
      if (ssnVal && ssnVal.length === 10) {
        const formattedSsn = `${ssnVal.substring(0, 6)}-${ssnVal.substring(6)}`;
        helpers.setValue(formattedSsn);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    if (numbersOnly) {
      value = value.replace(/[^0-9]/g, ""); // only digits
    }

    if (numbersAndDecimalsOnly) {
      value = value.replace(/[^0-9.,]/g, ""); // digits, dot, comma allowed
      // ensure only one dot or comma
      const parts = value.split(/[.,]/);
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }
    }

    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength); // maxLength
      e.target.value = value;
    }

    helpers.setValue(value); // Update Formik state
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement> & { data?: string }) => {
    const data = (e as any).data as string | undefined;

    if (numbersOnly && data && /\D/.test(data)) {
      e.preventDefault();
    }

    if (numbersAndDecimalsOnly && data && /[^0-9.,]/.test(data)) {
      e.preventDefault();
    }
  };

  const handleClearField = () => {
    helpers.setValue("");
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");

    if (numbersOnly || numbersAndDecimalsOnly) {
      const cleaned = numbersOnly
        ? text.replace(/\D+/g, "")
        : text.replace(/[^0-9.,]+/g, "");

      if (text !== cleaned) {
        e.preventDefault();
        const input = e.target as HTMLInputElement;
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        const next = input.value.slice(0, start) + cleaned + input.value.slice(end);
        input.value = next;
        helpers.setValue(next);
      }
    }
  };

  return (
    <>
      <div className="input-container">
        <label htmlFor={name} className="required">
          <span>{label}</span>
          {moreinfo && (
            <span className="more-info">
              <span aria-hidden="true">{moreinfo}</span>
              {ssn && (
                <span className="show-for-sr">
                  i formatet, år, månad, dag och de fyra sista siffrorna.
                </span>
              )}
            </span>
          )}
          {meta.error && (
            <span className="validation-error">
              {meta.error.toString()}{" "}
              {ssn && (
                <>
                  <span aria-hidden="true">(ÅÅMMDD-XXXX)</span>
                  <span className="show-for-sr">
                    i formatet, år, månad, dag och de fyra sista siffrorna.
                  </span>
                </>
              )}
            </span>
          )}
        </label>

        <div className={`input-wrapper ${ssn ? "short" : "long"} ${meta.error ? "has-error" : ""}`}        >
          <input
            required
            className={`${ssn ? "short" : "long"} ${meta.error ? "has-error" : ""} ${upperCase ? "uppercase" : ""}`}
            type={numbersOnly || numbersAndDecimalsOnly ? "text" : type}
            inputMode={numbersOnly || numbersAndDecimalsOnly ? "decimal" : inputMode}
            pattern={numbersOnly ? "\\d*" : undefined}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onBeforeInput={handleBeforeInput}
            onPaste={handlePaste}
            id={name}
            name={name}
            value={field.value}
            autoComplete={autocomplete}
            maxLength={maxLength}
          />

          {(field.value ?? "").length > 0 && (
            <button
              className="clear-button"
              onClick={handleClearField}
              type="button"
            >
              <div className="show-for-sr">Rensa fältet, {label}</div>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default InputText;
